/**
 * Azioni (Livello 2) — piani operativi Anno/Mese/Settimana · Blocco 4 · F Lifecycle.
 *
 * Layout:
 *   - Tab primario stato: Attive · Raggiunte · Annullate
 *   - Tab secondario livello (dentro Attive): Anno / Mese / Settimana
 *   - Card con badge stato + progress
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Calendar as CalendarIcon, ChevronRight, Zap,
  Award, Ban, RotateCcw, Check,
} from "lucide-react";
import { api } from "../lib/api";
import Tip from "../components/Tip";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import CreateAzioneModal from "../components/CreateAzioneModal";
import MetaAchieveModal from "../components/MetaAchieveModal";
import MetaCancelModal from "../components/MetaCancelModal";
import { celebrate } from "../lib/confetti";

const LEVELS = [
  { v: "annual",  label: "Anno",      color: "#C3DAF4" },
  { v: "monthly", label: "Mese",      color: "#F5C24A" },
  { v: "weekly",  label: "Settimana", color: "#8FBF8F" },
];
const STATUS_TABS = [
  { v: "active",    label: "Attive" },
  { v: "achieved",  label: "Raggiunte" },
  { v: "cancelled", label: "Annullate" },
];
const PRIORITY_LABELS = { low: "Bassa", medium: "Media", high: "Alta", critical: "Critica" };

export default function Azioni() {
  const [statusTab, setStatusTab] = useState("active");
  const [levelTab, setLevelTab] = useState("weekly");
  const [items, setItems] = useState([]);
  const [tree, setTree] = useState({ annual: [], monthly: [], weekly: [] });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [achieving, setAchieving] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const load = () => {
    setLoading(true);
    const params = statusTab === "active"
      ? { status: "active", level: levelTab }
      : { status: statusTab };
    Promise.all([
      api.listMete(params).catch(() => []),
      api.getMeteTree().catch(() => ({ annual: [], monthly: [], weekly: [] })),
    ]).then(([list, t]) => {
      setItems(list || []); setTree(t); setLoading(false);
    }).catch(() => { toast.error("Errore caricamento azioni"); setLoading(false); });
  };
  useEffect(() => { load(); }, [statusTab, levelTab]); // eslint-disable-line

  const activeCount = statusTab === "active"
    ? (tree.annual.length + tree.monthly.length + tree.weekly.length)
    : items.length;

  return (
    <div className="space-y-5" data-testid="azioni-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest opacity-70">Come</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold inline-flex items-center gap-2">
            <Zap size={26}/> Azioni
          </h1>
          <p className="mt-2 text-sm opacity-80 max-w-2xl">
            Piani operativi che collegano le tue <b>Mete</b> ai <b>Cicli</b> di ogni giorno.
            Anno → Mese → Settimana: il progresso sale da solo quando completi i cicli collegati.
          </p>
        </div>
        <Tip label="Crea nuova azione">
          <button onClick={() => setShowCreate(true)} className="pxos-btn-primary text-sm" data-testid="azioni-new">
            <Plus size={14}/> Nuova azione
          </button>
        </Tip>
      </div>

      <Tabs value={statusTab} onValueChange={setStatusTab} data-testid="azioni-status-tabs">
        <TabsList className="w-full sm:w-auto">
          {STATUS_TABS.map((t) => (
            <TabsTrigger key={t.v} value={t.v} data-testid={`azioni-status-${t.v}`}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="active" className="mt-4">
          <Tabs value={levelTab} onValueChange={setLevelTab} data-testid="azioni-level-tabs">
            <TabsList className="w-full sm:w-auto">
              {LEVELS.map((l) => (
                <TabsTrigger key={l.v} value={l.v} data-testid={`azioni-level-${l.v}`}>
                  {l.label}
                  <span className="ml-1.5 opacity-60 text-[10px]">
                    ({(tree[l.v] || []).length})
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
            {LEVELS.map((l) => (
              <TabsContent key={l.v} value={l.v} className="mt-3">
                <CardList
                  loading={loading}
                  items={items.filter((m) => m.status === "active" && m.level === l.v)}
                  level={l}
                  onAchieve={setAchieving}
                  onCancel={setCancelling}
                />
              </TabsContent>
            ))}
          </Tabs>
        </TabsContent>

        <TabsContent value="achieved" className="mt-4">
          <CardList
            loading={loading}
            items={items}
            level={null}
            onRestore={(a) => restoreAndReload(a.id)}
          />
        </TabsContent>

        <TabsContent value="cancelled" className="mt-4">
          <CardList
            loading={loading}
            items={items}
            level={null}
            onRestore={(a) => restoreAndReload(a.id)}
          />
        </TabsContent>
      </Tabs>

      {showCreate && (
        <CreateAzioneModal
          defaultLevel={levelTab}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
          tree={tree}
        />
      )}
      {achieving && (
        <MetaAchieveModal
          entity={{ title: achieving.title }}
          showNotes={false}
          onClose={() => setAchieving(null)}
          onConfirm={async ({ achieved_date }) => {
            await api.achieveMeta(achieving.id, { achieved_date });
            setAchieving(null); load(); celebrate({ durationMs: 1600 });
            toast.success("🎯 Azione raggiunta");
          }}
        />
      )}
      {cancelling && (
        <MetaCancelModal
          entity={{ title: cancelling.title }}
          onClose={() => setCancelling(null)}
          onConfirm={async ({ reason }) => {
            await api.cancelMeta(cancelling.id, reason);
            setCancelling(null); load();
            toast.success("Azione annullata");
          }}
        />
      )}
    </div>
  );

  async function restoreAndReload(id) {
    try { await api.restoreMeta(id); toast.success("Azione riportata attiva"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  }
}

function CardList({ loading, items, level, onAchieve, onCancel, onRestore }) {
  if (loading) return <div className="opacity-60 text-sm py-10 text-center">Caricamento…</div>;
  if (items.length === 0) return (
    <div className="pxos-card p-8 text-center opacity-60 text-sm">Nessuna azione in questa vista.</div>
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {items.map((a) => (
        <AzioneCard key={a.id} azione={a}
          level={level || LEVELS.find((l) => l.v === a.level)}
          onAchieve={onAchieve ? () => onAchieve(a) : null}
          onCancel={onCancel ? () => onCancel(a) : null}
          onRestore={onRestore ? () => onRestore(a) : null}
        />
      ))}
    </div>
  );
}

function AzioneCard({ azione, level, onAchieve, onCancel, onRestore }) {
  const pct = Number(azione.cached_progress || 0);
  const barColor = pct >= 100 ? "#3F8F5C" : pct >= 60 ? "#8FBF8F" : pct >= 30 ? "#F5C24A" : "#95BAEF";
  const deadline = azione.deadline_date ? new Date(azione.deadline_date + "T00:00:00") : null;
  const accent = level?.color || "#e5e7eb";

  const dateFmt = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT") : null;

  return (
    <Link to={`/azioni/${azione.id}`}
          className="block border rounded-xl p-4 bg-white hover:shadow-sm transition"
          style={{ borderColor: "var(--pxos-border)", borderLeft: `4px solid ${accent}` }}
          data-testid="azione-card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-lg font-semibold truncate flex-1" data-testid="azione-title">{azione.title}</div>
            {azione.status === "achieved" && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                    style={{ background: "#DCFCE7", color: "#166534" }}
                    data-testid="azione-badge-achieved">
                <Check size={9}/> Raggiunta{dateFmt(azione.achieved_at) ? ` · ${dateFmt(azione.achieved_at)}` : ""}
              </span>
            )}
            {azione.status === "cancelled" && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                    style={{ background: "#F3F4F6", color: "#4B5563" }}
                    data-testid="azione-badge-cancelled">
                <Ban size={9}/> Annullata{dateFmt(azione.cancelled_at) ? ` · ${dateFmt(azione.cancelled_at)}` : ""}
              </span>
            )}
          </div>
          {azione.description && (
            <div className="text-xs opacity-70 mt-1 line-clamp-2">{azione.description}</div>
          )}
          {azione.status === "cancelled" && azione.cancelled_reason && (
            <div className="text-xs opacity-70 mt-1 italic line-clamp-2" data-testid="azione-cancel-reason">
              Motivo: {azione.cancelled_reason}
            </div>
          )}
        </div>
        <ChevronRight size={16} className="opacity-40 shrink-0 mt-1"/>
      </div>

      {azione.status !== "cancelled" && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-[11px] opacity-70 mb-1">
            <span>Progresso</span>
            <span className="font-mono font-semibold" data-testid="azione-progress-pct">{pct.toFixed(0)}%</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden bg-black/5">
            <div className="h-full transition-all"
                 style={{ width: `${Math.min(100, pct)}%`, background: barColor }}
                 data-testid="azione-progress-bar"/>
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-[11px] opacity-70">
          {deadline && (
            <span className="inline-flex items-center gap-1"><CalendarIcon size={11}/>
              {deadline.toLocaleDateString("it-IT")}
            </span>
          )}
          {azione.priority !== "medium" && (
            <span className="pxos-badge">{PRIORITY_LABELS[azione.priority]}</span>
          )}
        </div>
        <div className="flex gap-1" onClick={(e) => e.preventDefault()}>
          {onAchieve && (
            <button onClick={(e) => { e.preventDefault(); onAchieve(); }}
                    className="rounded-full border px-2.5 py-1 text-[11px] inline-flex items-center gap-1"
                    style={{ borderColor: "#8FBF8F", color: "#2F6B3F" }}
                    data-testid="azione-achieve-btn">
              <Award size={11}/> Raggiunta
            </button>
          )}
          {onCancel && (
            <button onClick={(e) => { e.preventDefault(); onCancel(); }}
                    className="rounded-full border px-2.5 py-1 text-[11px] opacity-70"
                    data-testid="azione-cancel-btn">
              Annulla
            </button>
          )}
          {onRestore && (
            <button onClick={(e) => { e.preventDefault(); onRestore(); }}
                    className="rounded-full border px-2.5 py-1 text-[11px] inline-flex items-center gap-1"
                    data-testid="azione-restore-btn">
              <RotateCcw size={11}/> Ripristina
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}
